import { clsx } from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { type ToastInput, type ToastTone, toast, useToasts } from '@/lib/toast';
import { uiCopy } from './copy';
import { onTopLayerChange, raisePopover, supportsPopover } from './topLayer';

/**
 * Toasts at the top of the screen (the bottom is the thumb zone). Same store as `@/lib/toast`,
 * so `toast()` from anywhere lands here. The host is a manual popover in the top layer and
 * re-raises itself whenever a Sheet or Modal opens, so a toast is never hidden behind a sheet.
 * Without popover support it falls back to a fixed layer at --z-toast.
 *
 *   <ToastHost />                                    once, near the root
 *   const { toast } = useToast();
 *   toast({ title: 'Bucket removed. The ref has spoken.', action: { label: 'Undo', onPress: undo } });
 *
 * The shell mounts it once in `app/App.tsx`. Do not mount a second one.
 */

const TONE: Record<ToastTone, string> = {
  info: 'bg-white',
  success: 'bg-mint',
  warning: 'bg-butter',
  danger: 'bg-peach',
};

/** A glyph per tone, so the tone reads without color. */
const GLYPH: Record<ToastTone, string> = { info: 'i', success: '✓', warning: '!', danger: '×' };

export function useToast() {
  const dismiss = useToasts((s) => s.dismiss);
  const show = useCallback((t: ToastInput) => toast(t), []);
  return { toast: show, dismiss };
}

export function ToastHost({ offsetTop = 'calc(var(--tts-header-h) + 0.5rem)' }: { offsetTop?: string }) {
  const items = useToasts((s) => s.items);
  const dismiss = useToasts((s) => s.dismiss);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    raisePopover(el);
    return onTopLayerChange(() => raisePopover(el));
  }, []);

  const popover = typeof document !== 'undefined' && supportsPopover(document.createElement('div'));

  return (
    <div
      ref={ref}
      popover={popover ? 'manual' : undefined}
      aria-live="polite"
      className={clsx(
        'pointer-events-none m-0 flex flex-col items-center gap-2 border-0 bg-transparent text-ink',
        'fixed inset-x-0 top-0 z-(--z-toast) h-auto w-full max-w-none overflow-visible px-4 pb-0',
        // A closed popover is display:none; the manual one is always shown, so pin it open.
        'open:flex',
      )}
      style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${offsetTop})`, inset: '0 0 auto 0' }}
    >
      {items.map((t) => (
        <div
          key={t.id}
          role={t.tone === 'danger' ? 'alert' : 'status'}
          className={clsx(
            'tts-card animate-tts-drop pointer-events-auto flex w-full max-w-md items-center gap-3 px-3 py-2.5',
            TONE[t.tone],
          )}
        >
          <span
            aria-hidden="true"
            className="grid size-7 shrink-0 place-items-center rounded-chip border-2 border-ink bg-white font-display text-base font-black"
          >
            {GLYPH[t.tone]}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-bold leading-snug">{t.title}</p>
            {t.body && <p className="text-sm">{t.body}</p>}
          </div>
          {t.action ? (
            <button
              type="button"
              className="tts-btn min-h-11 shrink-0 px-3 text-sm"
              onClick={() => {
                t.action?.onPress();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : (
            <button
              type="button"
              aria-label={uiCopy.dismiss}
              className="grid size-11 shrink-0 place-items-center rounded-tts font-display text-xl font-black"
              onClick={() => dismiss(t.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
